/* eslint-disable react/prop-types */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function BACKEND_BASE() {
  const raw = process.env.REACT_APP_BACKEND_URL || "";
  return raw.replace(/\/$/, "");
}

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return BACKEND_BASE() + u;
}

/**
 * Settings section: story-card assets & phrase pools.
 * Owned by the parent `draft` state via `value` + `onChange`.
 */
export default function StoryCardsSection({ value, onChange }) {
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);

  const patch = (p) => onChange({ ...value, ...p });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const { url } = await api.uploadCardLogo(file);
      patch({ card_logo_url: url });
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err.message || "Upload failed"
      );
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const uploadBg = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingBg(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const { id, url } = await api.uploadCardBackground(f);
        uploaded.push({
          id: id || url,
          url,
          label: f.name.replace(/\.[^.]+$/, ""),
        });
      }
      patch({ card_backgrounds: [...(value.card_backgrounds || []), ...uploaded] });
      toast.success(`Uploaded ${uploaded.length} background${uploaded.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err.message || "Upload failed"
      );
    } finally {
      setUploadingBg(false);
      if (bgInputRef.current) bgInputRef.current.value = "";
    }
  };

  const removeBg = (id) =>
    patch({
      card_backgrounds: (value.card_backgrounds || []).filter((b) => b.id !== id),
    });

  const updateBgLabel = (id, label) =>
    patch({
      card_backgrounds: (value.card_backgrounds || []).map((b) =>
        b.id === id ? { ...b, label } : b
      ),
    });

  const updatePhraseList = (key, i, v) => {
    const next = [...(value[key] || [])];
    next[i] = v;
    patch({ [key]: next });
  };

  const addPhrase = (key) =>
    patch({ [key]: [...(value[key] || []), ""] });

  const removePhrase = (key, i) => {
    const next = [...(value[key] || [])];
    next.splice(i, 1);
    patch({ [key]: next });
  };

  return (
    <div className="space-y-6">
      {/* Card size + margin */}
      <div>
        <div className="overline mb-1.5">Primary card size &amp; margin (px)</div>
        <div className="grid gap-3 sm:grid-cols-2 max-w-lg">
          <NumField
            label="Width"
            testId="card-size-width"
            value={value.card_size?.width ?? 1080}
            min={200}
            max={4000}
            onChange={(n) =>
              patch({ card_size: { ...(value.card_size || {}), width: n } })
            }
          />
          <NumField
            label="Height"
            testId="card-size-height"
            value={value.card_size?.height ?? 1920}
            min={200}
            max={4000}
            onChange={(n) =>
              patch({ card_size: { ...(value.card_size || {}), height: n } })
            }
          />
          {["top", "right", "bottom", "left"].map((side) => (
            <NumField
              key={side}
              label={`Margin — ${side}`}
              testId={`card-padding-${side}`}
              value={value.card_padding?.[side] ?? 96}
              min={0}
              max={500}
              onChange={(n) =>
                patch({
                  card_padding: { ...(value.card_padding || {}), [side]: n },
                })
              }
            />
          ))}
        </div>
        <p className="text-[11px] text-[var(--ink-muted)] mt-2">
          Default: 1080×1920 (Instagram Story). Margin is the empty space
          between the card edge and the chip / logo / headline / footer.
        </p>
      </div>

      {/* Secondary card size (Instagram feed post 4:5) */}
      <div>
        <div className="overline mb-1.5">
          Secondary card size (Instagram post) (px)
        </div>
        <div className="grid gap-3 sm:grid-cols-2 max-w-lg">
          <NumField
            label="Width"
            testId="card-size-alt-width"
            value={value.card_size_alt?.width ?? 1080}
            min={200}
            max={4000}
            onChange={(n) =>
              patch({
                card_size_alt: { ...(value.card_size_alt || {}), width: n },
              })
            }
          />
          <NumField
            label="Height"
            testId="card-size-alt-height"
            value={value.card_size_alt?.height ?? 1350}
            min={200}
            max={4000}
            onChange={(n) =>
              patch({
                card_size_alt: { ...(value.card_size_alt || {}), height: n },
              })
            }
          />
        </div>
        <p className="text-[11px] text-[var(--ink-muted)] mt-2">
          Default: 1080×1350 (Instagram feed post, 4:5). The article page
          shows a second &ldquo;Download PNG&rdquo; button at this size for
          feed-style shares. Layout is anchored top &amp; bottom so the same
          card design stays proportional at either aspect ratio.
        </p>
      </div>

      {/* Logo */}
      <div>
        <div className="overline mb-1.5">Property logo (PNG, transparent background)</div>
        <div className="flex items-center gap-3">
          {value.card_logo_url ? (
            <div className="flex items-center gap-3">
              <div
                className="border border-[var(--border)] bg-neutral-900 p-2 flex items-center justify-center"
                style={{ height: 80, minWidth: 140 }}
              >
                <img
                  src={resolveUrl(value.card_logo_url)}
                  alt="logo"
                  className="max-h-[64px] max-w-[220px] object-contain"
                />
              </div>
              <button
                type="button"
                data-testid="card-logo-remove"
                onClick={() => patch({ card_logo_url: null })}
                className="text-xs px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <X className="w-3 h-3 inline mr-1" />
                Remove
              </button>
            </div>
          ) : (
            <span className="text-xs text-[var(--ink-muted)] italic">
              No logo uploaded yet.
            </span>
          )}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/svg+xml"
            onChange={uploadLogo}
            className="hidden"
            data-testid="card-logo-input"
          />
          <button
            type="button"
            data-testid="card-logo-upload"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
          >
            {uploadingLogo ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            {value.card_logo_url ? "Replace logo" : "Upload logo"}
          </button>
        </div>
      </div>

      {/* Generic backgrounds */}
      <div>
        <div className="overline mb-1.5">Generic backgrounds</div>
        <p className="text-xs text-[var(--ink-muted)] mb-2">
          Uploaded images that editors can pick instead of the article&rsquo;s
          own image when generating a card. Portrait or square works best.
        </p>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 mb-2">
          {(value.card_backgrounds || []).map((b) => (
            <div key={b.id} className="border border-[var(--border)] bg-white p-2 flex flex-col gap-1.5">
              <div className="relative aspect-[9/16] bg-neutral-900 overflow-hidden">
                <img
                  src={resolveUrl(b.url)}
                  alt={b.label || ""}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeBg(b.id)}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full p-1"
                  data-testid={`card-bg-remove-${b.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <input
                type="text"
                value={b.label || ""}
                onChange={(e) => updateBgLabel(b.id, e.target.value)}
                placeholder="Label"
                className="w-full px-2 py-1 text-xs border border-[var(--border)] outline-none"
              />
            </div>
          ))}
        </div>
        <input
          ref={bgInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={uploadBg}
          className="hidden"
          data-testid="card-bg-input"
        />
        <button
          type="button"
          data-testid="card-bg-upload"
          onClick={() => bgInputRef.current?.click()}
          disabled={uploadingBg}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
        >
          {uploadingBg ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          Add background(s)
        </button>
      </div>

      {/* Phrase pools */}
      <PhrasePool
        label="Top-left chip phrases (randomly picked)"
        testIdPrefix="card-chip"
        keyName="card_top_chips"
        list={value.card_top_chips || []}
        onEdit={updatePhraseList}
        onAdd={addPhrase}
        onRemove={removePhrase}
      />
      <PhrasePool
        label="Above-headline tag phrases (randomly picked)"
        testIdPrefix="card-tag"
        keyName="card_headline_tags"
        list={value.card_headline_tags || []}
        onEdit={updatePhraseList}
        onAdd={addPhrase}
        onRemove={removePhrase}
      />
    </div>
  );
}

function NumField({ label, testId, value, min = 0, max = 4000, onChange }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
        {label}
      </span>
      <input
        type="number"
        data-testid={testId}
        value={value}
        min={min}
        max={max}
        step={1}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
        }}
        className="px-2 py-1 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
      />
    </label>
  );
}

function PhrasePool({ label, testIdPrefix, keyName, list, onEdit, onAdd, onRemove }) {
  return (
    <div>
      <div className="overline mb-1.5">{label}</div>
      <ul className="space-y-1 mb-2">
        {list.map((phrase, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              type="text"
              data-testid={`${testIdPrefix}-input-${i}`}
              value={phrase}
              onChange={(e) => onEdit(keyName, i, e.target.value)}
              className="flex-1 min-w-0 px-2 py-1 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] font-mono-display uppercase"
              placeholder="NEWS UPDATE"
            />
            <button
              type="button"
              data-testid={`${testIdPrefix}-remove-${i}`}
              onClick={() => onRemove(keyName, i)}
              className="p-1 text-[var(--ink-muted)] hover:text-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid={`${testIdPrefix}-add`}
        onClick={() => onAdd(keyName)}
        className="text-xs px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)]"
      >
        + Add phrase
      </button>
    </div>
  );
}
